import React, { useState, useEffect } from 'react';

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made cookie choices
    const savedPreferences = localStorage.getItem('cookiePreferences');
    const bannerDismissed = localStorage.getItem('cookieBannerDismissed');
    
    if (!savedPreferences && !bannerDismissed) {
      setShowBanner(true);
    } else if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleCookieChange = (type, value) => {
    const newPreferences = { ...cookiePreferences, [type]: value };
    setCookiePreferences(newPreferences);
  };

  const acceptAllCookies = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieBannerDismissed', 'true');
    setShowBanner(false);
    
    // Initialize analytics and marketing cookies
    initializeCookies(allAccepted);
  };

  const acceptSelectedCookies = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    localStorage.setItem('cookieBannerDismissed', 'true');
    setShowBanner(false);
    
    // Initialize selected cookies
    initializeCookies(cookiePreferences);
  };

  const rejectAllCookies = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setCookiePreferences(onlyEssential);
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyEssential));
    localStorage.setItem('cookieBannerDismissed', 'true');
    setShowBanner(false);
    
    // Initialize only essential cookies
    initializeCookies(onlyEssential);
  };

  const initializeCookies = (preferences) => {
    // Initialize Google Analytics if analytics cookies are accepted
    if (preferences.analytics && typeof window !== 'undefined') {
      // Google Analytics initialization
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted'
        });
      }
    }

    // Initialize marketing cookies if accepted
    if (preferences.marketing && typeof window !== 'undefined') {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          ad_storage: 'granted'
        });
      }
    }

    // Initialize preference cookies if accepted
    if (preferences.preferences) {
      // Store user preferences
      localStorage.setItem('userPreferences', JSON.stringify({
        theme: 'light',
        language: 'en',
        fontSize: 'medium'
      }));
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-blue-500 shadow-2xl animate-slide-up">
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🍪</span>
              <h3 className="text-lg font-semibold text-gray-900">
                We use cookies to enhance your experience
              </h3>
            </div>
            <p className="text-gray-700 text-sm mb-3">
              We use cookies and similar technologies to improve our website, analyze usage, and personalize content. 
              You can manage your preferences below or learn more in our{' '}
              <a href="/cookies" className="text-blue-600 hover:text-blue-800 underline">
                Cookie Policy
              </a>.
            </p>
            
            {/* Cookie Preferences */}
            <div className="flex flex-wrap gap-4 text-xs">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={cookiePreferences.essential}
                  disabled
                  className="mr-1"
                />
                <span className="text-gray-600">Essential (Required)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={cookiePreferences.analytics}
                  onChange={(e) => handleCookieChange('analytics', e.target.checked)}
                  className="mr-1"
                />
                <span className="text-gray-600">Analytics</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={cookiePreferences.marketing}
                  onChange={(e) => handleCookieChange('marketing', e.target.checked)}
                  className="mr-1"
                />
                <span className="text-gray-600">Marketing</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={cookiePreferences.preferences}
                  onChange={(e) => handleCookieChange('preferences', e.target.checked)}
                  className="mr-1"
                />
                <span className="text-gray-600">Preferences</span>
              </label>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={rejectAllCookies}
              className="px-4 py-2 text-cyan-300 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
              Reject All
            </button>
            <button
              onClick={acceptSelectedCookies}
              className="px-4 py-2 bg-blue-600 text-cyan-300 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Save Preferences
            </button>
            <button
              onClick={acceptAllCookies}
              className="px-4 py-2 bg-green-600 text-cyan-300 rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
